<!-- views/CourseStudioView.vue - Updated with loading states -->
<template>
  <div class="studio-container">
    <div class="studio-sidebar">
      <div class="studio-header">
        <h2>🎓 Course Studio</h2>
        <button
          class="btn-primary"
          @click="createNewCourse"
          :disabled="courseStore.creating"
        >
          <span v-if="courseStore.creating" class="spinner-small"></span>
          <span v-else>
            <svg
              width="20"
              height="20"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              stroke-width="2"
            >
              <path d="M12 5v14M5 12h14" />
            </svg>
            New Course
          </span>
        </button>
      </div>

      <div class="course-list">
        <div
          v-for="course in courseStore.courses"
          :key="course.id"
          class="course-item"
          :class="{ active: selectedCourse?.id === course.id }"
          @click="selectCourse(course.id)"
        >
          <div class="course-thumbnail">
            <img
              v-if="course.thumbnail"
              :src="course.thumbnail"
              :alt="course.title"
            />
            <div v-else class="thumbnail-placeholder">
              <span>{{ course.title?.charAt(0) || "📚" }}</span>
            </div>
          </div>
          <div class="course-info">
            <h4>{{ course.title || "Untitled Course" }}</h4>
            <span
              class="course-status"
              :class="course.isPublished ? 'published' : 'draft'"
            >
              {{ course.isPublished ? "Published" : "Draft" }}
            </span>
          </div>
        </div>

        <div
          v-if="courseStore.loading && courseStore.courses.length === 0"
          class="loading-state"
        >
          <div class="spinner-small"></div>
          <p>Loading courses...</p>
        </div>

        <div v-else-if="courseStore.courses.length === 0" class="empty-state">
          <svg
            width="64"
            height="64"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            stroke-width="1.5"
          >
            <path
              d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253"
            />
          </svg>
          <h3>No courses yet</h3>
          <p>Create your first course and start sharing your knowledge!</p>
          <button class="btn-primary" @click="createNewCourse">
            Create Course
          </button>
        </div>
      </div>
    </div>

    <div class="studio-main">
      <div v-if="courseStore.loading && !selectedCourse" class="loading-center">
        <div class="spinner"></div>
        <p>Loading course...</p>
      </div>

      <div v-else-if="selectedCourse" class="studio-content">
        <!-- Course Management Tabs -->
        <div class="studio-tabs">
          <button
            v-for="tab in tabs"
            :key="tab.id"
            class="tab"
            :class="{ active: activeTab === tab.id }"
            @click="activeTab = tab.id"
          >
            <span class="tab-icon">{{ tab.icon }}</span>
            {{ tab.label }}
          </button>
        </div>

        <!-- Tab Content -->
        <div class="tab-content">
          <!-- Content Tab -->
          <div v-if="activeTab === 'content'" class="content-tab">
            <CourseContentManager
              :course="selectedCourse"
              @update="handleContentUpdate"
            />
          </div>

          <!-- Statistics Tab -->
          <div v-else-if="activeTab === 'stats'" class="stats-tab">
            <CourseStatistics :course="selectedCourse" />
          </div>

          <!-- Settings Tab -->
          <div v-else-if="activeTab === 'settings'" class="settings-tab">
            <CourseSettings
              :course="selectedCourse"
              @update="handleSettingsUpdate"
            />
          </div>

          <!-- Preview Tab -->
          <div v-else-if="activeTab === 'preview'" class="preview-tab">
            <CoursePreview :course="selectedCourse" />
          </div>
        </div>
      </div>

      <div v-else class="empty-selection">
        <svg
          width="80"
          height="80"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          stroke-width="1.5"
        >
          <rect x="2" y="3" width="20" height="14" rx="2" ry="2" />
          <line x1="8" y1="21" x2="16" y2="21" />
          <line x1="12" y1="17" x2="12" y2="21" />
        </svg>
        <h3>Select a course to start editing</h3>
        <p>Choose a course from the sidebar or create a new one</p>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, onMounted, onUnmounted } from "vue";
import { useRouter } from "vue-router";
import { useAuthStore } from "@/stores/auth";
import { useCourseStore } from "@/stores/course";
import CourseContentManager from "@/components/courses/CourseContentManager.vue";
import CourseStatistics from "@/components/courses/CourseStatistics.vue";
import CourseSettings from "@/components/courses/CourseSettings.vue";
import CoursePreview from "@/components/courses/CoursePreview.vue";

const router = useRouter();
const authStore = useAuthStore();
const courseStore = useCourseStore();

const selectedCourse = ref(null);
const activeTab = ref("content");

const tabs = [
  { id: "content", label: "Content", icon: "📝" },
  { id: "stats", label: "Statistics", icon: "📊" },
  { id: "settings", label: "Settings", icon: "⚙️" },
  { id: "preview", label: "Preview", icon: "👁️" },
];

// Load courses on mount - use the creator endpoint
onMounted(async () => {
  if (authStore.user) {
    await courseStore.fetchCreatorCourses(authStore.user.uid);
    if (courseStore.courses.length > 0) {
      selectCourse(courseStore.courses[0].id);
    }
  }
});

// Update selectCourse to use the edit endpoint
const selectCourse = async (courseId) => {
  if (selectedCourse.value?.id === courseId) return;

  const result = await courseStore.fetchCourseForEdit(courseId);
  if (result.success) {
    selectedCourse.value = result.course;
  }
};

// Clean up on unmount
onUnmounted(() => {
  courseStore.resetState();
});

const createNewCourse = async () => {
  if (courseStore.creating) return;

  const result = await courseStore.createCourse({
    creatorId: authStore.user.uid,
    creatorName: authStore.user.displayName,
    title: "New Course",
    description: "Describe your course...",
  });

  if (result.success) {
    selectedCourse.value = result.course;
    // Refresh the course list
    await courseStore.fetchCreatorCourses(authStore.user.uid);
  }
};

const handleContentUpdate = () => {
  if (selectedCourse.value) {
    courseStore.fetchCourse(selectedCourse.value.id);
  }
};

const handleSettingsUpdate = () => {
  if (selectedCourse.value) {
    courseStore.fetchCourse(selectedCourse.value.id);
  }
};
</script>

<style lang="scss" scoped>
$primary-orange: #ff8000;
$primary-blue: #001829;

.spinner-small {
  display: inline-block;
  width: 18px;
  height: 18px;
  border: 2px solid rgba(255, 255, 255, 0.3);
  border-top: 2px solid #fff;
  border-radius: 50%;
  animation: spin 0.6s linear infinite;
}

.loading-state {
  text-align: center;
  padding: 40px 20px;
  color: #868e96;

  .spinner-small {
    display: block;
    margin: 0 auto 12px;
    width: 32px;
    height: 32px;
    border: 3px solid rgba(255, 128, 0, 0.2);
    border-top: 3px solid $primary-orange;
  }
}

.loading-center {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  height: 100%;
  color: #868e96;

  .spinner {
    width: 48px;
    height: 48px;
    border: 4px solid #e9ecef;
    border-top: 4px solid $primary-orange;
    border-radius: 50%;
    animation: spin 1s linear infinite;
    margin-bottom: 16px;
  }
}

.btn-primary:disabled {
  opacity: 0.7;
  cursor: not-allowed;
}

@keyframes spin {
  to {
    transform: rotate(360deg);
  }
}

.studio-container {
  display: flex;
  height: calc(100vh - 72px);
  margin-top: 72px;
  background: #f5f7fa;
}

.studio-sidebar {
  width: 320px;
  background: #fff;
  border-right: 1px solid #e9ecef;
  display: flex;
  flex-direction: column;
  overflow: hidden;

  .studio-header {
    padding: 20px;
    border-bottom: 1px solid #e9ecef;
    display: flex;
    justify-content: space-between;
    align-items: center;

    h2 {
      font-size: 20px;
      margin: 0;
      color: #1a1a1a;
    }

    .btn-primary {
      display: flex;
      align-items: center;
      gap: 6px;
      padding: 8px 16px;
      background: $primary-orange;
      color: #fff;
      border: none;
      border-radius: 8px;
      font-weight: 600;
      cursor: pointer;
      transition: all 0.3s ease;
      font-size: 14px;

      &:hover {
        background: darken($primary-orange, 10%);
        transform: translateY(-1px);
      }
    }
  }

  .course-list {
    flex: 1;
    overflow-y: auto;
    padding: 12px;

    .course-item {
      display: flex;
      gap: 12px;
      padding: 12px;
      border-radius: 8px;
      cursor: pointer;
      transition: all 0.2s ease;
      margin-bottom: 8px;

      &:hover {
        background: #f8f9fa;
      }

      &.active {
        background: rgba(255, 128, 0, 0.1);
        border: 1px solid $primary-orange;
      }

      .course-thumbnail {
        width: 64px;
        height: 48px;
        border-radius: 4px;
        overflow: hidden;
        flex-shrink: 0;
        background: #e9ecef;

        img {
          width: 100%;
          height: 100%;
          object-fit: cover;
        }

        .thumbnail-placeholder {
          width: 100%;
          height: 100%;
          display: flex;
          align-items: center;
          justify-content: center;
          background: linear-gradient(135deg, $primary-orange, #ffa500);
          color: #fff;
          font-size: 24px;
        }
      }

      .course-info {
        flex: 1;
        min-width: 0;

        h4 {
          margin: 0 0 4px 0;
          font-size: 14px;
          color: #1a1a1a;
          white-space: nowrap;
          overflow: hidden;
          text-overflow: ellipsis;
        }

        .course-status {
          font-size: 12px;
          padding: 2px 8px;
          border-radius: 12px;
          font-weight: 600;

          &.published {
            background: #d4edda;
            color: #155724;
          }

          &.draft {
            background: #fff3cd;
            color: #856404;
          }
        }
      }
    }

    .empty-state {
      text-align: center;
      padding: 40px 20px;

      svg {
        color: #adb5bd;
        margin-bottom: 16px;
      }

      h3 {
        color: #1a1a1a;
        margin-bottom: 8px;
      }

      p {
        color: #868e96;
        margin-bottom: 16px;
      }
    }
  }
}

.studio-main {
  flex: 1;
  overflow: hidden;

  .studio-content {
    height: 100%;
    display: flex;
    flex-direction: column;
    background: #fff;
  }

  .studio-tabs {
    display: flex;
    gap: 0;
    padding: 0 24px;
    border-bottom: 2px solid #e9ecef;
    background: #fff;

    .tab {
      padding: 16px 20px;
      background: none;
      border: none;
      font-size: 14px;
      font-weight: 600;
      color: #868e96;
      cursor: pointer;
      transition: all 0.2s ease;
      display: flex;
      align-items: center;
      gap: 8px;
      border-bottom: 3px solid transparent;
      margin-bottom: -2px;

      &:hover {
        color: #1a1a1a;
      }

      &.active {
        color: $primary-orange;
        border-bottom-color: $primary-orange;
      }

      .tab-icon {
        font-size: 18px;
      }
    }
  }

  .tab-content {
    flex: 1;
    overflow-y: auto;
    padding: 24px;
  }

  .empty-selection {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    height: 100%;
    color: #adb5bd;

    svg {
      margin-bottom: 16px;
    }

    h3 {
      color: #1a1a1a;
      margin-bottom: 8px;
    }

    p {
      color: #868e96;
    }
  }
}

/* Scrollbar styling */
.studio-sidebar .course-list::-webkit-scrollbar,
.tab-content::-webkit-scrollbar {
  width: 6px;
}

.studio-sidebar .course-list::-webkit-scrollbar-track,
.tab-content::-webkit-scrollbar-track {
  background: transparent;
}

.studio-sidebar .course-list::-webkit-scrollbar-thumb,
.tab-content::-webkit-scrollbar-thumb {
  background: #ddd;
  border-radius: 3px;
}

.studio-sidebar .course-list::-webkit-scrollbar-thumb:hover,
.tab-content::-webkit-scrollbar-thumb:hover {
  background: #ccc;
}
</style>
